import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './Contact.css';

const SUBJECTS = ['Student Enquiry', 'Instructor Enquiry', 'General Question', 'Complaint / Feedback'];

function Contact() {
  const blank = { fullName: '', email: '', phone: '', subject: '', message: '', agreePrivacy: false };
  const [formData, setFormData] = useState(blank);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const h = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.fullName.trim()) e.fullName = 'Full name is required';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Valid email is required';
    if (!formData.subject) e.subject = 'Please select a subject';
    if (!formData.message.trim()) e.message = 'Message is required';
    if (!formData.agreePrivacy) e.agreePrivacy = 'You must agree to the privacy policy';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post('/contact', {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ct-page">
        <div className="ct-success">
          <span className="ct-success-icon">✓</span>
          <h2>Message Sent!</h2>
          <p>Thanks for reaching out. We typically respond within 24 hours.</p>
          <button className="ct-success-btn" onClick={() => { setSubmitted(false); setFormData(blank); }}>Send Another Message</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ct-page">

      {/* ─── Hero ─── */}
      <div className="ct-hero">
        <div className="ct-hero-inner">
          <span className="ct-eyebrow">Get In Touch</span>
          <h1>Contact Fast Track Driving School</h1>
          <p>Questions, enquiries, or just want to say hi? We're here to help.</p>
        </div>
      </div>

      {/* ─── Layout ─── */}
      <div className="ct-layout">

        {/* Info Panel */}
        <aside className="ct-info">
          <div className="ct-info-card">
            <h2>How Can We Help?</h2>
            <p>Fill in the form and we'll respond as soon as possible — usually within 24 hours on business days.</p>

            <div className="ct-info-items">
              <div className="ct-info-item">
                <span className="ct-info-icon">📧</span>
                <div>
                  <strong>Email Us</strong>
                  <span>contact@fasttrackds.co.uk</span>
                </div>
              </div>
              <div className="ct-info-item">
                <span className="ct-info-icon">⏰</span>
                <div>
                  <strong>Response Time</strong>
                  <span>Within 24 hours</span>
                </div>
              </div>
              <div className="ct-info-item">
                <span className="ct-info-icon">🌍</span>
                <div>
                  <strong>Coverage</strong>
                  <span>Greater London &amp; surrounding areas</span>
                </div>
              </div>
            </div>

            <div className="ct-quick-links">
              <p className="ct-quick-title">Quick links</p>
              <a href="/book-lesson" className="ct-quick-btn">📅 Book a Lesson</a>
              <a href="/become-instructor" className="ct-quick-btn">🏅 Become an Instructor</a>
              <Link to="/refund-request" className="ct-quick-btn ct-quick-btn--refund">💷 Request a Refund</Link>
            </div>
          </div>
        </aside>

        {/* Form */}
        <main className="ct-form-wrap">
          <form onSubmit={handleSubmit} className="ct-form" noValidate>

            <div className="ct-form-grid">
              <div className={`ct-field${errors.fullName ? ' ct-field--err' : ''}`}>
                <label htmlFor="fullName">Full Name *</label>
                <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={h} placeholder="Jane Smith" />
                {errors.fullName && <span className="ct-err">{errors.fullName}</span>}
              </div>

              <div className={`ct-field${errors.phone ? ' ct-field--err' : ''}`}>
                <label htmlFor="phone">Phone (optional)</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={h} placeholder="07123 456789" />
              </div>

              <div className={`ct-field ct-field--full${errors.email ? ' ct-field--err' : ''}`}>
                <label htmlFor="email">Email Address *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={h} placeholder="jane@example.com" />
                {errors.email && <span className="ct-err">{errors.email}</span>}
              </div>

              <div className={`ct-field ct-field--full${errors.subject ? ' ct-field--err' : ''}`}>
                <label htmlFor="subject">Subject *</label>
                <select id="subject" name="subject" value={formData.subject} onChange={h}>
                  <option value="">— Select a subject —</option>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.subject && <span className="ct-err">{errors.subject}</span>}
              </div>

              <div className={`ct-field ct-field--full${errors.message ? ' ct-field--err' : ''}`}>
                <label htmlFor="message">Message *</label>
                <textarea id="message" name="message" value={formData.message} onChange={h} rows={6} placeholder="Tell us how we can help…" />
                {errors.message && <span className="ct-err">{errors.message}</span>}
              </div>
            </div>

            <div className={`ct-checkbox-row${errors.agreePrivacy ? ' ct-checkbox-row--err' : ''}`}>
              <input type="checkbox" id="agreePrivacy" name="agreePrivacy" checked={formData.agreePrivacy} onChange={h} />
              <label htmlFor="agreePrivacy">I agree to the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> *</label>
            </div>
            {errors.agreePrivacy && <span className="ct-err">{errors.agreePrivacy}</span>}

            {submitError && <div className="ct-err" style={{ marginTop: '0.5rem' }}>{submitError}</div>}
            <button type="submit" className="ct-submit-btn" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Message →'}
            </button>

          </form>
        </main>

      </div>
    </div>
  );
}

export default Contact;


// function Contact() {
//   const [formData, setFormData] = useState({
//     fullName: '',
//     email: '',
//     subject: '',
//     message: '',
//     agreePrivacy: false
//   });

//   const [errors, setErrors] = useState({});
//   const [submitted, setSubmitted] = useState(false);

//   const subjects = [
//     'Student Enquiry',
//     'Instructor Enquiry',
//     'General Question'
//   ];

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });
//     if (errors[name]) {
//       setErrors({ ...errors, [name]: '' });
//     }
//   };

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.fullName.trim()) {
//       newErrors.fullName = 'Full name is required';
//     }

//     if (!formData.email.trim()) {
//       newErrors.email = 'Email is required';
//     } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
//       newErrors.email = 'Email is invalid';
//     }

//     if (!formData.subject) {
//       newErrors.subject = 'Please select a subject';
//     }

//     if (!formData.message.trim()) {
//       newErrors.message = 'Message is required';
//     }

//     if (!formData.agreePrivacy) {
//       newErrors.agreePrivacy = 'You must agree to the privacy policy';
//     }

//     return newErrors;
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const newErrors = validateForm();

//     if (Object.keys(newErrors).length === 0) {
//       console.log('Contact form submitted:', formData);
//       setSubmitted(true);
//     } else {
//       setErrors(newErrors);
//     }
//   };

//   if (submitted) {
//     return (
//       <div className="contact">
//         <div className="success-message">
//           <div className="success-icon">✓</div>
//           <h2>Message Sent Successfully!</h2>
//           <p>Thank you for contacting Fast Track Driving School. We'll respond to your enquiry as soon as possible, typically within 24 hours.</p>
//           <button 
//             className="btn btn-primary" 
//             onClick={() => {
//               setSubmitted(false);
//               setFormData({
//                 fullName: '',
//                 email: '',
//                 subject: '',
//                 message: '',
//                 agreePrivacy: false
//               });
//             }}
//           >
//             Send Another Message
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="contact">
//       <div className="contact-header">
//         <h1>Contact Fast Track Driving School</h1>
//         <p>Get in touch with us today!</p>
//       </div>

//       <div className="contact-container">
//         <div className="contact-info">
//           <h2>Get in Touch</h2>
//           <p>Have a question or need assistance? Fill out the form and we'll get back to you as soon as possible.</p>
          
//           <div className="info-items">
//             <div className="info-item">
//               <div className="info-icon">📧</div>
//               <div>
//                 <h3>Email Us</h3>
//                 <p>info@fasttracktdriving.co.uk</p>
//               </div>
//             </div>
//             <div className="info-item">
//               <div className="info-icon">⏰</div>
//               <div>
//                 <h3>Response Time</h3>
//                 <p>Within 24 hours</p>
//               </div>
//             </div>
//             <div className="info-item">
//               <div className="info-icon">🌍</div>
//               <div>
//                 <h3>Coverage</h3>
//                 <p>UK-Wide Service</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="contact-form-wrapper">
//           <form onSubmit={handleSubmit} className="contact-form">
//             <div className="form-group">
//               <label htmlFor="fullName">Full Name *</label>
//               <input
//                 type="text"
//                 id="fullName"
//                 name="fullName"
//                 value={formData.fullName}
//                 onChange={handleChange}
//                 placeholder="Your Name"
//                 className={errors.fullName ? 'error' : ''}
//               />
//               {errors.fullName && <span className="error-message">{errors.fullName}</span>}
//             </div>

//             <div className="form-group">
//               <label htmlFor="email">Email Address *</label>
//               <input
//                 type="email"
//                 id="email"
//                 name="email"
//                 value={formData.email}
//                 onChange={handleChange}
//                 placeholder="your.email@example.com"
//                 className={errors.email ? 'error' : ''}
//               />
//               {errors.email && <span className="error-message">{errors.email}</span>}
//             </div>

//             <div className="form-group">
//               <label htmlFor="subject">Subject *</label>
//               <select
//                 id="subject"
//                 name="subject"
//                 value={formData.subject}
//                 onChange={handleChange}
//                 className={errors.subject ? 'error' : ''}
//               >
//                 <option value="">Select an Option</option>
//                 {subjects.map((subject) => (
//                   <option key={subject} value={subject}>{subject}</option>
//                 ))}
//               </select>
//               {errors.subject && <span className="error-message">{errors.subject}</span>}
//             </div>

//             <div className="form-group">
//               <label htmlFor="message">Message *</label>
//               <textarea
//                 id="message"
//                 name="message"
//                 value={formData.message}
//                 onChange={handleChange}
//                 placeholder="Tell us how we can help you..."
//                 rows="6"
//                 className={errors.message ? 'error' : ''}
//               ></textarea>
//               {errors.message && <span className="error-message">{errors.message}</span>}
//             </div>

//             <div className="form-group checkbox-group">
//               <label className="checkbox-label">
//                 <input
//                   type="checkbox"
//                   name="agreePrivacy"
//                   checked={formData.agreePrivacy}
//                   onChange={handleChange}
//                   className={errors.agreePrivacy ? 'error' : ''}
//                 />
//                 <span>
//                   I agree to the <a href="/privacy" target="_blank">Privacy Policy</a> *
//                 </span>
//               </label>
//               {errors.agreePrivacy && <span className="error-message">{errors.agreePrivacy}</span>}
//             </div>

//             <button type="submit" className="btn btn-primary btn-large btn-submit">
//               Send Message
//             </button>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Contact;
